import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { curationsService } from '../lib/curations'

interface UseCurationMembershipsProps {
  currentUserId?: string
  itemIds: string[]
}

interface CurationMembership {
  issue_id: string
  contribution_id: string
}

/**
 * Hook to batch-fetch curation memberships for multiple items.
 * This avoids N+1 queries by fetching all memberships in a single query.
 */
export function useCurationMemberships({ currentUserId, itemIds }: UseCurationMembershipsProps) {
  const [userCurations, setUserCurations] = useState<any[]>([])
  const [membershipMap, setMembershipMap] = useState<Map<string, Set<string>>>(new Map())
  const [isLoading, setIsLoading] = useState(false)

  const [defaultCurationId, setDefaultCurationId] = useState<string | null>(null)

  // Fetch user curations once (including default for save detection)
  useEffect(() => {
    const fetchCurations = async () => {
      if (!currentUserId) return

      try {
        // One query for everything on this curator (was two round-trips to the
        // same issues table): fetch all curations including the default, then
        // split locally — regular list for display, default id for save status.
        const all = await curationsService.getUserCurations(currentUserId, true)
        setUserCurations(all.filter((c: any) => !c.is_default))
        setDefaultCurationId(all.find((c: any) => c.is_default)?.id || null)
      } catch (err) {
        console.error('Error fetching user curations:', err)
      }
    }

    fetchCurations()
  }, [currentUserId])

  // Create stable key from itemIds to avoid unnecessary re-fetches
  const itemIdsKey = useMemo(() => itemIds.join(','), [itemIds])

  // Batch fetch memberships when itemIds change
  useEffect(() => {
    const fetchMemberships = async () => {
      if (!currentUserId || itemIds.length === 0) {
        setMembershipMap(new Map())
        return
      }

      setIsLoading(true)
      try {
        // Build list of user's curation IDs to scope the query
        const userCurationIds = userCurations.map(c => c.id)
        if (defaultCurationId) userCurationIds.push(defaultCurationId)

        if (userCurationIds.length === 0) {
          setMembershipMap(new Map())
          return
        }

        // Single query for item memberships scoped to user's curations
        const { data, error } = await supabase
          .from('issue_items')
          .select('issue_id, contribution_id')
          .in('contribution_id', itemIds)
          .in('issue_id', userCurationIds)

        if (error) {
          console.error('Error fetching memberships:', error)
          return
        }

        // Build lookup map: itemId -> Set<curationId>
        const newMap = new Map<string, Set<string>>()
        data?.forEach((row: CurationMembership) => {
          if (!newMap.has(row.contribution_id)) {
            newMap.set(row.contribution_id, new Set())
          }
          newMap.get(row.contribution_id)!.add(row.issue_id)
        })

        setMembershipMap(newMap)
      } catch (err) {
        console.error('Error fetching memberships:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMemberships()
  }, [currentUserId, itemIds, itemIdsKey, userCurations, defaultCurationId])

  // Toggle item in curation (add or remove)
  const toggleCuration = useCallback(async (itemId: string, curationId: string) => {
    const currentMemberships = membershipMap.get(itemId) || new Set()
    const isInCuration = currentMemberships.has(curationId)

    try {
      if (isInCuration) {
        // Remove from curation
        const { data: itemToRemove } = await supabase
          .from('issue_items')
          .select('id, position')
          .eq('issue_id', curationId)
          .eq('contribution_id', itemId)
          .single()

        if (itemToRemove) {
          await curationsService.removeCurationItem(itemToRemove.id)

          // Update cover if we removed the first item
          if (itemToRemove.position === 0) {
            await curationsService.updateCoverImageToFirstItem(curationId)
          }
        }

        // Update local state
        setMembershipMap(prev => {
          const newMap = new Map(prev)
          const itemSet = new Set(newMap.get(itemId) || [])
          itemSet.delete(curationId)
          newMap.set(itemId, itemSet)
          return newMap
        })
      } else {
        // Add to curation — use max position to avoid gaps from prior deletions
        const { data: items } = await supabase
          .from('issue_items')
          .select('position')
          .eq('issue_id', curationId)
          .order('position', { ascending: false })
          .limit(1)

        const nextPosition = items && items.length > 0 ? items[0].position + 1 : 0

        await curationsService.addCurationItem(curationId, {
          contribution_id: itemId,
          position: nextPosition
        })

        // Update cover image if this is the first item
        if (nextPosition === 0) {
          await curationsService.updateCoverImageToFirstItem(curationId)
        }

        // Update local state
        setMembershipMap(prev => {
          const newMap = new Map(prev)
          const itemSet = new Set(newMap.get(itemId) || [])
          itemSet.add(curationId)
          newMap.set(itemId, itemSet)
          return newMap
        })
      }
    } catch (err: any) {
      console.error('Error toggling curation:', err)
      throw err
    }
  }, [membershipMap])

  // Create new curation and add item to it
  const createCurationWithItem = useCallback(async (title: string, itemId: string) => {
    if (!currentUserId) return

    try {
      const curation = await curationsService.createCuration(currentUserId, {
        title: title.trim(),
        layout_type: 'grid',
        rounded_corners: false
      })

      // Add to curations list
      setUserCurations(prev => [curation, ...prev])

      // Add the item to the new curation
      await toggleCuration(itemId, curation.id)

      return curation
    } catch (err: any) {
      console.error('Error creating curation:', err)
      throw err
    }
  }, [currentUserId, toggleCuration])

  // Get memberships for a specific item
  const getMembershipsForItem = useCallback((itemId: string): Set<string> => {
    return membershipMap.get(itemId) || new Set()
  }, [membershipMap])

  // Check if an item is saved (exists in user's default curation)
  const isSaved = useCallback((itemId: string): boolean => {
    if (!defaultCurationId) return false
    const memberships = membershipMap.get(itemId)
    return memberships ? memberships.has(defaultCurationId) : false
  }, [membershipMap, defaultCurationId])

  return {
    userCurations,
    membershipMap,
    defaultCurationId,
    isLoading,
    toggleCuration,
    createCurationWithItem,
    getMembershipsForItem,
    isSaved
  }
}
